/**
 * Per-row actions menu: archive / unarchive only. Publish is editor-only — the index row carries no
 * `data`, so the client publish gate cannot be honoured from here. Unarchive returns to `DRAFT`.
 */
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { AssignmentTemplateListRow } from './interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useSidebarPrivileges } from '@entities/viewer'

import { AssignmentStatus } from './enums'
import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplateUpdateApi } from '../../api/assignment-templates/update.hook'
import { useAssignmentTemplatesApi } from '../../api/assignment-templates/list.hook'
import { getAssignmentEditPath } from './assignment-template-options'

interface UseAssignmentTemplateActionsOptions {
  template: ComputedRef<AssignmentTemplateListRow>
}

interface AssignmentTemplateActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  refCanManage: ComputedRef<boolean>
  refIsArchived: ComputedRef<boolean>
  toggle: () => void
  openEdit: () => Promise<void>
  openDuplicate: () => void
  openDelete: () => void
  archive: () => Promise<void>
  unarchive: () => Promise<void>
}

export const useAssignmentTemplateActions = (
  { template }: UseAssignmentTemplateActionsOptions,
): AssignmentTemplateActionsReturn => {
  const router = useRouter()
  const store = useAssignmentTemplatesStore()
  const { canManageAssignmentTemplates } = useSidebarPrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const refCanManage = computed<boolean>(() => canManageAssignmentTemplates.value)
  const refIsArchived = computed<boolean>(() => template.value.status === AssignmentStatus.Archived)

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openEdit = async (): Promise<void> => {
    isOpen.value = false
    await router.push(getAssignmentEditPath(template.value.id))
  }

  const openDuplicate = (): void => {
    isOpen.value = false
    store.setTemplateToDuplicate(template.value)
  }

  const openDelete = (): void => {
    isOpen.value = false
    store.setTemplateToDelete(template.value)
  }

  /** Refetched rather than patched in place: `updated_at` and `is_stale` are server-derived. */
  const setStatus = async (status: AssignmentStatus): Promise<void> => {
    isOpen.value = false

    const updated = await useAssignmentTemplateUpdateApi({
      templateId: template.value.id,
      payload: { status },
    })

    if (!updated) return

    await useAssignmentTemplatesApi()
  }

  const archive = async (): Promise<void> => {
    await setStatus(AssignmentStatus.Archived)
  }

  /** Back to `DRAFT`, never to `PUBLISHED`. */
  const unarchive = async (): Promise<void> => {
    await setStatus(AssignmentStatus.Draft)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    refCanManage,
    refIsArchived,
    toggle,
    openEdit,
    openDuplicate,
    openDelete,
    archive,
    unarchive,
  }
}
